/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as MsDyn365 from '@msdyn365-commerce/core';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    onTelemetryClick,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IHerbalSignoutData } from './herbal-signout.data';
import { IHerbalSignoutConfig, IHerbalSignoutProps } from './herbal-signout.props.autogenerated';

export interface IHeaderViewProps extends IHerbalSignoutProps<IHerbalSignoutData> {
    HeaderTag: IModuleProps;
    HeaderContainer: INodeProps;
    HeaderTopBarContainer: INodeProps;

    signOutLink?: React.ReactNode;
}

/**
 *
 * Header component.
 * @extends {React.PureComponent<IHerbalSignoutProps<IHerbalSignoutData>>}
 */
@observer
class Header extends React.PureComponent<IHerbalSignoutProps<IHerbalSignoutData>> {
    private readonly telemetryContent: ITelemetryContent;

    @computed public get displayName(): Readonly<string | undefined> {
        const customer = this.props.data.accountInformation?.result;
        return customer ? customer.FirstName || customer.Name : undefined;
    }

    constructor(props: IHerbalSignoutProps<IHerbalSignoutData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    // eslint-disable-next-line complexity -- Disabled complexity.
    public render(): JSX.Element | null {
        const {
            data: { accountInformation },
            context: {
                request: { user }
            }
        } = this.props;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- app config is generic
        const headerClassName = classnames('ms-button-signout', this.props.config.className);
        const customer = accountInformation && accountInformation.result;

        if (!customer) {
            return null;
        }

        const viewProps: IHeaderViewProps = {
            ...(this.props as IHerbalSignoutProps<IHerbalSignoutData>),
            ...this.state,
            HeaderTag: {
                moduleProps: this.props,
                className: classnames(headerClassName),
                tag: 'div'
            },
            HeaderContainer: {
                className: classnames('ms-button-signout__container')
            },
            HeaderTopBarContainer: {
                className: classnames('ms-button-signout__footer')
            },
            signOutLink: this._getSignOutButton(customer, user, this.props.config)
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _getSignOutButton(
        customer: Customer | undefined,
        user: MsDyn365.IRequestContextUser | undefined,
        config: IHerbalSignoutConfig
    ): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignOut);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);

        return (
            customer &&
            this.displayName && (
                <Button
                    className='ms-header__signout-button'
                    title={config.buttonSignoutName}
                    href={(user && user.signOutUrl) || ''}
                    aria-label={config.buttonSignoutName}
                    onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Sign out')}
                    {...attributes}
                >
                    <span className='ms-header__signout-button-text' aria-hidden>
                        {config.buttonSignoutName}
                    </span>
                </Button>
            )
        );
    }
}

export default Header;
